import { Tag } from '@/modules/Tag';
import { Icon } from '@/modules/Icon';
import { Visual } from '@/modules/Visual/Index';
import { isValidUrl } from '@/modules/Avatar/utils';
import { Popup } from '@/modules/Popup';
import { Tooltip } from '@/modules/Tooltip';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { AnyIconDefinition, IconFontColor, IconType, VisualSlotType } from '@/shared/types/types';
import type { DropdownOption, DropdownTagsOverflow } from '@/shared/types/dropdown';
import { faChevronUp, faChevronDown, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';

const TAG_GAP_PX = 4;

/** Estimación ancho del chip +n (px); se recalibra con el ancho real al pintar. */
function estimateOverflowChipWidth(hiddenCount: number): number {
  if (hiddenCount <= 0) return 0;
  return 28 + String(hiddenCount).length * 9;
}

function computeVisibleTagCount(widths: number[], available: number): number {
  const n = widths.length;
  if (n === 0 || available <= 0) return 0;

  for (let k = n; k >= 0; k--) {
    const hidden = n - k;
    let sum = 0;
    for (let i = 0; i < k; i++) {
      sum += widths[i];
      if (i < k - 1) sum += TAG_GAP_PX;
    }
    const chipW = hidden > 0 ? estimateOverflowChipWidth(hidden) : 0;
    const gapBeforeChip = k > 0 && hidden > 0 ? TAG_GAP_PX : 0;
    if (sum + gapBeforeChip + chipW <= available) {
      return k;
    }
  }
  return 0;
}

function useSingleLineVisibleCount(
  enabled: boolean,
  selectedOptions: DropdownOption[],
  tagsAreaRef: RefObject<HTMLDivElement | null>,
  measureRef: RefObject<HTMLDivElement | null>,
  selectionKey: string,
): number {
  const [visibleCount, setVisibleCount] = useState(selectedOptions.length);

  const recalc = useCallback(() => {
    if (!enabled) {
      setVisibleCount(selectedOptions.length);
      return;
    }
    const area = tagsAreaRef.current;
    const measure = measureRef.current;
    if (!area || !measure) {
      setVisibleCount(selectedOptions.length);
      return;
    }
    const available = area.clientWidth;
    const children = measure.children;
    const n = children.length;
    if (n === 0) {
      setVisibleCount(0);
      return;
    }
    const widths: number[] = [];
    for (let i = 0; i < n; i++) {
      widths.push((children[i] as HTMLElement).getBoundingClientRect().width);
    }
    setVisibleCount(computeVisibleTagCount(widths, available));
  }, [enabled, selectedOptions.length, selectionKey]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const el = tagsAreaRef.current;
    if (!enabled || !el) return;
    const ro = new ResizeObserver(() => {
      recalc();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled, recalc]);

  return enabled ? visibleCount : selectedOptions.length;
}

interface TagsMultipleProps {
  label: string;
  placeholder: string;
  selectedOptions: DropdownOption[];
  handleRemoveSelected: (option: string | number) => void;
  toggleDropdown: (e?: React.MouseEvent<HTMLDivElement>) => void;
  isOpen: boolean;
  isOpening: boolean;
  setIsOpening: (isOpening: boolean) => void;
  customInputRef: React.RefObject<HTMLDivElement>;
  disabled: boolean;
  error: string;
  helperText: string;
  rightSlot: VisualSlotType | null;
  valueName: string;
  idName: string;
  size: 'xs' | 'sm' | 'md' | 'lg';
  tagsOverflow?: DropdownTagsOverflow;
  tagsOverflowPopoverPlacement?: 'top' | 'bottom';
  overflowPopoverZIndex: number;
  tagsOverflowPopoverTitle?: string;
  /** X del campo: vacía toda la selección (mismas reglas que `Dropdown.showTriggerClearButton`). */
  showClearButton?: boolean;
  onClearAll?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Icono a la izquierda (mismo criterio que `Input` / rama no-tags del `Dropdown`). */
  leftSlot?: VisualSlotType | null;
  leftSlotClassName?: string;
  colorLeftIcon?: IconFontColor;
  /** Alineado con `popover.fullWidth`: ancho completo del contenedor vs. `ui-w-fit`. @default true */
  fullWidth?: boolean;
}

export const TagsMultiple = ({
  label,
  placeholder,
  selectedOptions,
  handleRemoveSelected,
  toggleDropdown,
  isOpen,
  isOpening,
  setIsOpening,
  customInputRef,
  disabled,
  error,
  helperText,
  rightSlot,
  valueName,
  idName,
  size,
  tagsOverflow = 'wrap',
  tagsOverflowPopoverPlacement = 'bottom',
  overflowPopoverZIndex,
  tagsOverflowPopoverTitle,
  showClearButton = true,
  onClearAll,
  leftSlot = null,
  leftSlotClassName,
  colorLeftIcon,
  fullWidth = true,
}: TagsMultipleProps) => {
  const singleLine = tagsOverflow === 'single-line';
  const tagsAreaRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowTriggerRef = useRef<HTMLDivElement>(null);
  /** Evita abrir el menú del Dropdown al pulsar el chip +n (focus/click). */
  const suppressDropdownFromOverflowRef = useRef(false);

  const selectionKey = selectedOptions
    .map((o) => (o ? String((o as Record<string, unknown>)[idName]) : ''))
    .join('|');

  const [needsMorePadding, setNeedsMorePadding] = useState(false);
  const [initialHeight, setInitialHeight] = useState<number | null>(null);
  const [popoverMaxWidthPx, setPopoverMaxWidthPx] = useState<number | null>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);

  /** Ancho del campo (fila con borde) = ancho del dropdown; el panel +n no debe superarlo. */
  useLayoutEffect(() => {
    if (!singleLine || !customInputRef?.current) {
      setPopoverMaxWidthPx(null);
      return;
    }
    const el = customInputRef.current;
    const update = () => {
      setPopoverMaxWidthPx(el.getBoundingClientRect().width);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [singleLine, customInputRef]);

  const visibleCount = useSingleLineVisibleCount(
    singleLine,
    selectedOptions,
    tagsAreaRef,
    measureRef,
    selectionKey,
  );

  useEffect(() => {
    if (singleLine || !tagsContainerRef.current || selectedOptions.length === 0) {
      setNeedsMorePadding(false);
      setInitialHeight(null);
      return;
    }

    const checkOverflow = () => {
      const container = tagsContainerRef.current;
      if (container) {
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        if (initialHeight === null) {
          setInitialHeight(clientHeight);
          return;
        }

        const isOverflowing = scrollHeight > initialHeight;
        setNeedsMorePadding(isOverflowing);
      }
    };

    const timeoutId = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedOptions.length, initialHeight, singleLine]);

  const sizesClassesWrap = {
    xs: `ui-px-8 ui-py-4 ${needsMorePadding ? 'ui-min-h-control-xs' : 'ui-h-control-xs'}`,
    sm: `ui-px-8 ui-py-4 ${needsMorePadding ? 'ui-min-h-control-sm' : 'ui-h-control-sm'}`,
    md: `ui-p-8 ${needsMorePadding ? 'ui-min-h-control-md' : 'ui-h-control-md'}`,
    lg: `ui-p-8 ${needsMorePadding ? 'ui-min-h-control-lg' : 'ui-h-control-lg'}`,
  };

  const sizesClassesSingle = {
    xs: 'ui-h-control-xs ui-min-h-control-xs ui-px-8 ui-py-4',
    sm: 'ui-h-control-sm ui-min-h-control-sm ui-px-8 ui-py-4',
    md: 'ui-h-control-md ui-min-h-control-md ui-p-8',
    lg: 'ui-h-control-lg ui-min-h-control-lg ui-p-8',
  };

  const sizesClasses = singleLine ? sizesClassesSingle : sizesClassesWrap;

  const renderTag = (option: DropdownOption, keySuffix?: string) => {
    if (!option) return null;
    const optionId = (option as Record<string, unknown>)[idName] as string | number;
    const optionValue = (option as Record<string, unknown>)[valueName] as string | number;
    return (
      <Tag
        key={`${String(optionId)}${keySuffix ?? ''}`}
        className="ui-z-[1010] ui-shrink-0"
        label={String(optionValue)}
        color="accent"
        stroke={false}
        onClose={(e) => {
          e.stopPropagation();
          handleRemoveSelected(optionId);
        }}
      />
    );
  };

  const overflowOptions = singleLine ? selectedOptions.slice(visibleCount) : [];
  const hiddenCount = overflowOptions.length;
  const visibleOptions = singleLine ? selectedOptions.slice(0, visibleCount) : selectedOptions;

  const overflowOptionsSorted = useMemo(() => {
    if (!singleLine || overflowOptions.length === 0) return [];
    return [...overflowOptions].sort((a, b) =>
      String((a as Record<string, unknown>)[valueName] ?? '').localeCompare(
        String((b as Record<string, unknown>)[valueName] ?? ''),
        'es',
        { sensitivity: 'base' },
      ),
    );
  }, [singleLine, overflowOptions, valueName]);

  /** Texto del panel +n: opciones no visibles en el chip, separadas por comas, orden alfabético. */
  const overflowLineQuoted = useMemo(() => {
    if (overflowOptionsSorted.length === 0) return '';
    return overflowOptionsSorted
      .map((option) => String((option as Record<string, unknown>)[valueName] ?? ''))
      .join(', ');
  }, [overflowOptionsSorted, valueName]);

  const showRightIcon = !!(rightSlot && typeof rightSlot === 'object' && 'icon' in rightSlot);
  const showClearControl =
    showClearButton && selectedOptions.length > 0 && !disabled && !!onClearAll;

  const isLeftSlotUrl = (slot: VisualSlotType | null | undefined): boolean => {
    if (!slot || typeof slot !== 'string') return false;
    if (slot.startsWith('data:') || slot.startsWith('blob:')) return true;
    return isValidUrl(slot);
  };
  const leftSlotIsUrl = isLeftSlotUrl(leftSlot);
  const showLeftIcon = Boolean(
    leftSlot && (leftSlotIsUrl || (typeof leftSlot === 'object' && 'icon' in leftSlot)),
  );

  return (
    <div
      className={cn(
        'ui-relative ui-flex ui-min-w-0 ui-flex-col ui-gap-1',
        fullWidth ? 'ui-w-full' : 'ui-w-fit',
      )}
    >
      {label && (
        <div className="ui-flex ui-flex-row ui-items-center ui-gap-1 ui-leading-tight">
          <label className="ui-text-label-md-regular ui-font-label-md-semibold ui-text-primary">
            {label}
          </label>
        </div>
      )}
      <div
        ref={customInputRef}
        data-dropdown-input="true"
        className={cn(
          `ui-group ui-flex ${sizesClasses[size]} ui-cursor-pointer ui-items-center ui-gap-1 ui-rounded-control-sm ui-border ui-border-default ui-bg-fill ui-py-4 ui-transition-[padding] ui-duration-150 ui-ease-out focus-within:ui-border-brand hover:ui-border-brand`,
          singleLine ? 'ui-relative ui-min-w-0 ui-overflow-hidden' : '',
        )}
        onPointerDownCapture={(e) => {
          if (singleLine && (e.target as HTMLElement).closest('[data-tags-overflow-chip]')) {
            suppressDropdownFromOverflowRef.current = true;
          }
        }}
        onClick={(e) => {
          if (isOpening) {
            return;
          }
          if ((e.target as HTMLElement).closest('[data-dropdown-clear-trigger]')) {
            return;
          }
          if (singleLine && (e.target as HTMLElement).closest('[data-tags-overflow-chip]')) {
            suppressDropdownFromOverflowRef.current = false;
            return;
          }
          if (suppressDropdownFromOverflowRef.current) {
            suppressDropdownFromOverflowRef.current = false;
            return;
          }
          toggleDropdown(e);
        }}
        onFocus={(e) => {
          // Evitar abrir al enfocar hijos (p. ej. botón X de limpiar o la X de un Tag).
          if (e.target !== e.currentTarget) {
            return;
          }
          if (suppressDropdownFromOverflowRef.current) {
            suppressDropdownFromOverflowRef.current = false;
            return;
          }
          if (!isOpen && !isOpening) {
            setIsOpening(true);
            toggleDropdown();
          }
        }}
        tabIndex={disabled ? -1 : 0}
      >
        {showLeftIcon && (
          <span
            className={cn(
              'ui-inline-flex ui-shrink-0 ui-leading-[0] ui-transition-colors ui-duration-150',
              colorLeftIcon
                ? ''
                : disabled
                  ? 'ui-text-disabled'
                  : error
                    ? 'ui-text-danger'
                    : 'ui-text-tertiary group-focus-within:ui-text-brand',
            )}
          >
            {leftSlotIsUrl && typeof leftSlot === 'string' ? (
              <Visual
                slot={leftSlot}
                size="xxs"
                defaultSizeIcon={false}
                colorIcon={colorLeftIcon}
                className={leftSlotClassName}
              />
            ) : (
              <Icon
                name={leftSlot as IconType}
                size="sm"
                color={colorLeftIcon || 'current'}
                className={leftSlotClassName}
              />
            )}
          </span>
        )}
        {selectedOptions.length > 0 ? (
          singleLine ? (
            <>
              <div
                ref={tagsAreaRef}
                className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-gap-1 ui-overflow-hidden"
              >
                {visibleOptions.map((option) => renderTag(option, '-v'))}
                {hiddenCount > 0 && (
                  <div
                    ref={overflowTriggerRef}
                    data-tags-overflow-chip
                    className="ui-flex ui-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <Tag
                      className="ui-z-[1010]"
                      label={`+${hiddenCount}`}
                      color="gray"
                      stroke={false}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  </div>
                )}
              </div>
              <div
                ref={measureRef}
                className="ui-pointer-events-none ui-absolute ui-left-0 ui-top-0 ui-z-[-1] ui-flex ui-max-w-none ui-flex-nowrap ui-gap-1 ui-opacity-0"
                aria-hidden
              >
                {selectedOptions.map((option) => renderTag(option, '-m'))}
              </div>
            </>
          ) : (
            <div
              ref={tagsContainerRef}
              className={`ui-flex ui-flex-wrap ui-gap-1 ${needsMorePadding ? 'ui-max-h-auto' : 'ui-max-h-[40px]'} ui-overflow-hidden`}
            >
              {selectedOptions.map((option) => renderTag(option))}
            </div>
          )
        ) : (
          <span className="ui-min-w-0 ui-flex-1 ui-text-body-md-regular ui-text-tertiary">
            {placeholder}
          </span>
        )}
        <div className="ui-ml-auto ui-flex ui-shrink-0 ui-items-center ui-gap-8">
          {showRightIcon && (
            <Icon name={rightSlot as AnyIconDefinition} size="sm" color="secondary" />
          )}

          <div className="ui-flex ui-items-center ui-gap-8">
            {showClearControl && (
              <>
                {showRightIcon && (
                  <div
                    className="ui-h-5 ui-w-[1px] ui-min-w-[1px] ui-shrink-0 ui-bg-[var(--ui-color-border-default)]"
                    aria-hidden
                  />
                )}
                <Tooltip label="Limpiar" position="top" preserveLayout>
                  <button
                    type="button"
                    aria-label="Limpiar"
                    data-dropdown-clear-trigger
                    className="ui-inline-flex ui-place-items-center ui-rounded-control-sm ui-text-secondary ui-transition-colors hover:ui-text-brand"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onClearAll(e);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <Icon name={faXmark} size="sm" color="current" />
                  </button>
                </Tooltip>
                <div
                  className="ui-h-5 ui-w-[1px] ui-min-w-[1px] ui-shrink-0 ui-bg-[var(--ui-color-border-default)]"
                  aria-hidden
                />
              </>
            )}

            <span className="ui-inline-flex ui-text-secondary ui-transition-colors hover:ui-text-brand">
              <Icon name={isOpen ? faChevronUp : faChevronDown} size="sm" color="current" />
            </span>
          </div>
        </div>
      </div>
      {error && <p className="ui-mt-1 ui-text-body-xs-regular ui-text-danger">{error}</p>}
      {helperText && (
        <p className="ui-mt-1 ui-text-body-xs-regular ui-text-tertiary">{helperText}</p>
      )}
      {singleLine && hiddenCount > 0 && (
        <Popup
          triggerRef={overflowTriggerRef}
          position={tagsOverflowPopoverPlacement === 'top' ? 'top' : 'bottom-center'}
          offset={8}
          popoverArrow="auto"
          closeOnClick={false}
          stopPropagationList
          style={{
            zIndex: overflowPopoverZIndex,
            ...(popoverMaxWidthPx !== null && popoverMaxWidthPx !== undefined
              ? {
                width: `${popoverMaxWidthPx}px`,
                maxWidth: `${popoverMaxWidthPx}px`,
                boxSizing: 'border-box',
                minWidth: 0,
              }
              : {}),
          }}
          className="ui-box-border ui-min-w-0"
          popoverStructured
          popoverTitle={tagsOverflowPopoverTitle}
          popoverShowCloseButton
        >
          <div className="ui-w-full ui-min-w-0">
            <p className="ui-m-0 ui-whitespace-normal ui-break-words ui-text-heading-h6 ui-font-heading-h6 ui-leading-heading-h6 ui-tracking-heading-h6 ui-text-primary">
              {overflowLineQuoted}
            </p>
          </div>
        </Popup>
      )}
    </div>
  );
};
